import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult
} from '@tanstack/react-query'
import { useAgentRuntime } from '../context/AgentProvider'
import { getGuestUserId } from '../utils/guestUserId'
import { DataWithCursor, AgentChat } from '@multiplayer-app/ai-agent-types'

const STALE_TIME = 30 * 1000
const GC_TIME = 5 * 60 * 1000
export const CHATS_PAGE_SIZE = 20

export const chatKeys = {
  all: ['mp-agents', 'chats'] as const,
  lists: () => [...chatKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...chatKeys.lists(), filters] as const,
  infinites: () => [...chatKeys.all, 'infinite'] as const,
  infinite: (filters: Record<string, unknown>) => [...chatKeys.infinites(), filters] as const,
  detail: (id: string | undefined) => [...chatKeys.all, 'detail', id] as const
} as const

export type UseChatsInfiniteListResult = UseInfiniteQueryResult<
  InfiniteData<DataWithCursor<AgentChat>, unknown>,
  Error
>

// Backwards-compatible alias for existing consumers (e.g. useChatBootstrap)
export type UseChatsResult = UseChatsInfiniteListResult

export interface UseChatsParams {
  pageSize?: number
  sortField?: string
  sortOrder?: 'asc' | 'desc'
  multiUser?: boolean
  /** When false, query is disabled */
  enabled?: boolean
}

export interface UseChatsListParams extends UseChatsParams {
  /** 1-based page number */
  page: number
}

export type UseChatsListResult = UseQueryResult<DataWithCursor<AgentChat>, Error> & {
  page: number
  pageSize: number
  totalPages: number
  total: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

/** Flatten all pages from an infinite chats query into a single array. */
export const flattenInfiniteChats = (
  data: InfiniteData<DataWithCursor<AgentChat>, unknown> | undefined
): AgentChat[] => data?.pages.flatMap((p) => p.data) ?? []

export const useChatsInfiniteList = (params?: UseChatsParams): UseChatsInfiniteListResult => {
  const runtime = useAgentRuntime()
  const userId = runtime.config.user?.id ?? getGuestUserId()
  const appId = runtime.config.appId
  const {
    pageSize = CHATS_PAGE_SIZE,
    sortOrder = 'desc',
    sortField = 'updatedAt',
    multiUser,
    enabled = true
  } = params ?? {}

  return useInfiniteQuery({
    queryKey: chatKeys.infinite({ userId, appId, sortField, sortOrder, pageSize, multiUser }),
    queryFn: async ({ pageParam }) => {
      const result = await runtime.transport.listChats({
        userId,
        skip: pageParam,
        limit: pageSize,
        multiUser,
        sortOrder,
        sortField
      })
      return result
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const { cursor, data } = lastPage
      const loaded = (cursor.skip ?? 0) + data.length
      return loaded < cursor.total ? loaded : undefined
    },
    enabled: enabled && Boolean(userId),
    staleTime: STALE_TIME,
    gcTime: GC_TIME
  })
}

// Preserve original hook name for infinite variant
export const useChats = useChatsInfiniteList

export const useChatsList = (params: UseChatsListParams): UseChatsListResult => {
  const runtime = useAgentRuntime()
  const userId = runtime.config.user?.id ?? getGuestUserId()
  const appId = runtime.config.appId
  const {
    page,
    pageSize = CHATS_PAGE_SIZE,
    sortOrder = 'desc',
    sortField = 'updatedAt',
    multiUser,
    enabled = true
  } = params

  const skip = (page - 1) * pageSize

  const query = useQuery({
    queryKey: chatKeys.list({ userId, appId, sortField, sortOrder, pageSize, page, multiUser }),
    queryFn: async () =>
      runtime.transport.listChats({
        userId,
        skip,
        limit: pageSize,
        multiUser,
        sortOrder,
        sortField
      }),
    enabled: enabled && Boolean(userId) && page >= 1,
    staleTime: STALE_TIME,
    gcTime: GC_TIME
  })

  const total = query.data?.cursor.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return {
    ...query,
    page,
    pageSize,
    totalPages,
    total,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1
  }
}